import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar as UAvatar, AvatarFallback as UAvatarFallback, AvatarImage as UAvatarImage } from '@/components/ui/avatar';
import { welcomeWeekService, type WelcomeWeekAdminContext } from '@/services/welcomeWeekService';
import { useAuthUser } from '@/hooks/useAuthUser';
import { DEFAULT_WW_SEASON_CODE, type WelcomeWeekGroup, type WelcomeWeekGroupAdmin } from '@/types/welcomeWeek';
import { ArrowLeft, CalendarDays, Check, Clock, Link2, Mail, Pencil, Plus, Settings, Trash2, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { seasonQuery } from './wwUi';

type Candidate = { id: string; display_name: string | null; avatar_url: string | null };

const adminDisplayName = (admin: WelcomeWeekGroupAdmin) =>
  admin.profiles?.display_name || admin.profiles?.email || 'Unknown admin';

export default function GroupsAdminPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const seasonCode = searchParams.get('season') || DEFAULT_WW_SEASON_CODE;
  const explicitSeason = searchParams.get('season');
  const [context, setContext] = useState<WelcomeWeekAdminContext | null>(null);
  const [groups, setGroups] = useState<WelcomeWeekGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviteEmails, setInviteEmails] = useState<Record<string, string>>({});
  const [invitingGroup, setInvitingGroup] = useState<string | null>(null);
  const [removingAdmin, setRemovingAdmin] = useState<string | null>(null);
  const [revokingInvite, setRevokingInvite] = useState<string | null>(null);
  const [newGroup, setNewGroup] = useState({ code: '', name: '', slug: '' });
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [suggestions, setSuggestions] = useState<Record<string, Candidate[]>>({});
  const [openSuggestionsFor, setOpenSuggestionsFor] = useState<string | null>(null);
  const [searchingFor, setSearchingFor] = useState<string | null>(null);
  const searchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const { profile } = useAuthUser();
  const canManage = useMemo(() => context?.role === 'owner' || context?.role === 'admin', [context?.role]);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      let adminContext = await welcomeWeekService.getAdminContext(slug, seasonCode);
      if (!adminContext.season && (adminContext.role === 'owner' || adminContext.role === 'admin')) {
        const season = await welcomeWeekService.ensureSeason(adminContext.organization.id, seasonCode);
        adminContext = { ...adminContext, season };
      }
      setContext(adminContext);
      if (adminContext.season) {
        setGroups(await welcomeWeekService.listGroups(adminContext.season.id));
      }
    } catch (error) {
      console.error('[GroupsAdmin] load failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [seasonCode, slug]);

  useEffect(() => {
    load();
  }, [load]);

  const createGroup = async (event: FormEvent) => {
    event.preventDefault();
    if (!context?.season || creating || !canManage) return;
    if (!newGroup.code.trim() || !newGroup.name.trim()) {
      toast.error('Code and name are required.');
      return;
    }

    setCreating(true);
    try {
      await welcomeWeekService.createGroup({
        organization_id: context.organization.id,
        season_id: context.season.id,
        code: newGroup.code,
        name: newGroup.name,
        slug: newGroup.slug || newGroup.code,
      });
      setNewGroup({ code: '', name: '', slug: '' });
      toast.success('Group created');
      await load();
    } catch (error) {
      console.error('[GroupsAdmin] create failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const startEditGroup = (group: WelcomeWeekGroup) => {
    setEditingGroupId(group.id);
    setEditName(group.name);
  };

  const cancelEditGroup = () => {
    setEditingGroupId(null);
    setEditName('');
  };

  const saveEditGroup = async (group: WelcomeWeekGroup) => {
    const name = editName.trim();
    if (savingEdit) return;
    if (!name) {
      toast.error('Name is required.');
      return;
    }
    if (name === group.name) {
      cancelEditGroup();
      return;
    }
    setSavingEdit(true);
    try {
      await welcomeWeekService.updateGroup(group.id, { name });
      toast.success('Group name updated');
      cancelEditGroup();
      await load();
    } catch (error) {
      console.error('[GroupsAdmin] update group failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update group');
    } finally {
      setSavingEdit(false);
    }
  };

  const addCandidate = async (group: WelcomeWeekGroup, candidate: Candidate) => {
    if (invitingGroup) return;
    setInvitingGroup(group.id);
    try {
      await welcomeWeekService.addExistingUserAsGroupAdmin(group.id, candidate.id);
      toast.success(`${candidate.display_name || 'User'} added as group admin`);
      setInviteEmails({ ...inviteEmails, [group.id]: '' });
      setSuggestions({ ...suggestions, [group.id]: [] });
      setOpenSuggestionsFor(null);
      await load();
    } catch (error) {
      console.error('[GroupsAdmin] add admin failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to add group admin');
    } finally {
      setInvitingGroup(null);
    }
  };

  const invite = async (group: WelcomeWeekGroup) => {
    const email = inviteEmails[group.id]?.trim();
    if (!email || invitingGroup) return;
    setInvitingGroup(group.id);
    try {
      await welcomeWeekService.inviteGroupAdmin(group, email);
      toast.success('Group admin invitation created');
      setInviteEmails({ ...inviteEmails, [group.id]: '' });
      setSuggestions({ ...suggestions, [group.id]: [] });
      await load();
    } catch (error) {
      console.error('[GroupsAdmin] invite failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to invite group admin');
    } finally {
      setInvitingGroup(null);
    }
  };

  const removeAdmin = async (group: WelcomeWeekGroup, admin: WelcomeWeekGroupAdmin) => {
    const name = adminDisplayName(admin);
    if (removingAdmin || !window.confirm(`Remove ${name} as an admin of ${group.name}?`)) return;
    setRemovingAdmin(`${group.id}:${admin.user_id}`);
    try {
      await welcomeWeekService.removeGroupAdmin(group.id, admin.user_id);
      toast.success('Group admin removed');
      await load();
    } catch (error) {
      console.error('[GroupsAdmin] remove admin failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to remove group admin');
    } finally {
      setRemovingAdmin(null);
    }
  };

  const revokeInvite = async (invitationId: string) => {
    if (revokingInvite) return;
    setRevokingInvite(invitationId);
    try {
      await welcomeWeekService.revokeGroupInvitation(invitationId);
      toast.success('Invitation revoked');
      await load();
    } catch (error) {
      console.error('[GroupsAdmin] revoke invite failed', error);
      toast.error(error instanceof Error ? error.message : 'Failed to revoke invitation');
    } finally {
      setRevokingInvite(null);
    }
  };

  const onInviteInputChange = (group: WelcomeWeekGroup, value: string) => {
    setInviteEmails({ ...inviteEmails, [group.id]: value });
    if (searchTimers.current[group.id]) clearTimeout(searchTimers.current[group.id]);
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions((prev) => ({ ...prev, [group.id]: [] }));
      setOpenSuggestionsFor(null);
      return;
    }
    setSearchingFor(group.id);
    setOpenSuggestionsFor(group.id);
    searchTimers.current[group.id] = setTimeout(async () => {
      try {
        const results = await welcomeWeekService.searchGroupAdminCandidates(group.id, q);
        setSuggestions((prev) => ({ ...prev, [group.id]: results }));
      } catch (err) {
        console.error('[GroupsAdmin] search failed', err);
      } finally {
        setSearchingFor((cur) => (cur === group.id ? null : cur));
      }
    }, 250);
  };



  return (
    <PublicLayout>
      <div className="container py-8 space-y-6">
        <div>
          <Button asChild variant="ghost" className="px-0">
            <Link to={`/o/${slug}/ww${seasonQuery(explicitSeason)}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Welcome Week
            </Link>
          </Button>
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{seasonCode}</Badge>
              {context?.role && <Badge variant="outline">{context.role}</Badge>}
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">Welcome Week Groups</h1>
            <p className="text-muted-foreground">{context?.organization.name || slug}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to={`/o/${slug}/ww/admin${seasonQuery(explicitSeason)}`}>
                <Settings className="h-4 w-4 mr-2" />
                Master Admin
              </Link>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CalendarDays className="h-10 w-10 animate-pulse text-muted-foreground" />
          </div>
        ) : !canManage ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">Owner or admin access is required.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Create Group
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createGroup} className="grid gap-4 md:grid-cols-[120px_1fr_180px_auto] md:items-end">
                  <div className="space-y-2">
                    <Label htmlFor="ww-group-code">Code</Label>
                    <Input
                      id="ww-group-code"
                      value={newGroup.code}
                      placeholder="G01"
                      onChange={(e) => setNewGroup({ ...newGroup, code: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ww-group-name">Name</Label>
                    <Input
                      id="ww-group-name"
                      value={newGroup.name}
                      onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ww-group-slug">Slug</Label>
                    <Input
                      id="ww-group-slug"
                      value={newGroup.slug}
                      placeholder="g01"
                      onChange={(e) => setNewGroup({ ...newGroup, slug: e.target.value })}
                    />
                  </div>
                  <Button type="submit" disabled={creating}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="grid gap-4">
              {groups.map((group) => {
                const admins = group.admins || [];
                const pendingInvitations = group.pendingInvitations || [];
                return (
                  <Card key={group.id}>
                    <CardContent className="p-5">
                      <div className="space-y-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge>{group.code}</Badge>
                              {editingGroupId === group.id ? (
                                <div className="flex items-center gap-1.5">
                                  <Input
                                    autoFocus
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveEditGroup(group);
                                      if (e.key === 'Escape') cancelEditGroup();
                                    }}
                                    className="h-8 w-48"
                                    disabled={savingEdit}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
                                    title="Save name"
                                    disabled={savingEdit}
                                    onClick={() => saveEditGroup(group)}
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                                    title="Cancel"
                                    disabled={savingEdit}
                                    onClick={cancelEditGroup}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <>
                                  <h2 className="font-semibold">{group.name}</h2>
                                  {canManage && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                      title="Edit group name"
                                      onClick={() => startEditGroup(group)}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                            {admins.length > 0 ? (
                              <div className="mt-2 space-y-2">
                                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                                  <Users className="h-3.5 w-3.5" />
                                  Group admins ({admins.length})
                                </div>
                                <ul className="space-y-1">
                                  {admins.map((admin) => (
                                    <li
                                      key={admin.user_id}
                                      className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5"
                                    >
                                      <UAvatar className="h-6 w-6">
                                        <UAvatarImage src={admin.profiles?.avatar_url || ''} />
                                        <UAvatarFallback>
                                          {(admin.profiles?.display_name || admin.profiles?.email || 'U')
                                            .charAt(0)
                                            .toUpperCase()}
                                        </UAvatarFallback>
                                      </UAvatar>
                                      <span className="flex-1 truncate text-sm">
                                        {adminDisplayName(admin)}
                                      </span>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                        title="Remove admin"
                                        disabled={removingAdmin === `${group.id}:${admin.user_id}`}
                                        onClick={() => removeAdmin(group, admin)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <p className="mt-1 text-sm text-muted-foreground">No group admin assigned</p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button asChild variant="outline" size="sm">
                              <Link to={`/o/${slug}/ww/${group.slug}${seasonQuery(explicitSeason)}`}>
                                <Settings className="h-4 w-4 mr-2" />
                                Edit Schedule
                              </Link>
                            </Button>
                            <Button asChild variant="outline" size="sm">
                              <a
                                href={`https://www.calone.com/${slug}/${group.slug || group.code}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <CalendarDays className="h-4 w-4 mr-2" />
                                Group Welcome Week Calendar
                              </a>
                            </Button>
                            {group.admin_invite_token && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  const url = `${window.location.origin}/ww/join/${group.admin_invite_token}`;
                                  try {
                                    await navigator.clipboard.writeText(url);
                                    toast.success('Invite link copied — anyone with the link becomes an admin of this group.');
                                  } catch {
                                    window.prompt('Copy invite link:', url);
                                  }
                                }}
                              >
                                <Link2 className="h-4 w-4 mr-2" />
                                Copy invite link
                              </Button>
                            )}
                          </div>
                        </div>

                        {pendingInvitations.length > 0 && (
                          <div className="space-y-2 rounded-md border border-dashed p-3">
                            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                              <Clock className="h-3.5 w-3.5" />
                              Pending invitations ({pendingInvitations.length})
                            </div>
                            <ul className="space-y-1">
                              {pendingInvitations.map((invite) => (
                                <li
                                  key={invite.invitation_id}
                                  className="flex flex-wrap items-center gap-2 text-sm"
                                >
                                  <span>{invite.email}</span>
                                  <Badge variant="outline" className="text-xs">
                                    Awaiting acceptance
                                  </Badge>
                                  {invite.created_at && (
                                    <span className="text-xs text-muted-foreground">
                                      invited {new Date(invite.created_at).toLocaleDateString()}
                                    </span>
                                  )}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="ml-auto h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                    title="Revoke invitation"
                                    disabled={revokingInvite === invite.invitation_id}
                                    onClick={() => revokeInvite(invite.invitation_id)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                          <div className="relative">
                            <Input
                              type="text"
                              placeholder="Search by name or enter email"
                              value={inviteEmails[group.id] || ''}
                              onChange={(e) => onInviteInputChange(group, e.target.value)}
                              onFocus={() => {
                                if ((suggestions[group.id]?.length || 0) > 0) setOpenSuggestionsFor(group.id);
                              }}
                              onBlur={() => setTimeout(() => setOpenSuggestionsFor((cur) => (cur === group.id ? null : cur)), 150)}
                            />
                            {openSuggestionsFor === group.id && ((suggestions[group.id]?.length || 0) > 0 || searchingFor === group.id) && (
                              <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                                {searchingFor === group.id && !(suggestions[group.id]?.length) ? (
                                  <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>
                                ) : (
                                  (suggestions[group.id] || []).map((s) => (
                                    <button
                                      key={s.id}
                                      type="button"
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent disabled:opacity-50"
                                      disabled={invitingGroup === group.id}
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => addCandidate(group, s)}
                                    >
                                      <UAvatar className="h-7 w-7">
                                        <UAvatarImage src={s.avatar_url || ''} />
                                        <UAvatarFallback>{(s.display_name || 'U').charAt(0).toUpperCase()}</UAvatarFallback>
                                      </UAvatar>
                                      <span className="text-sm">{s.display_name || 'Unnamed user'}</span>
                                    </button>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                          <Button onClick={() => invite(group)} disabled={invitingGroup === group.id}>
                            <Mail className="h-4 w-4 mr-2" />
                            Invite
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>
    </PublicLayout>
  );
}
